import { HeaderContainer } from "@/components/layout/HeaderContainer";
import { useModals } from "@/features/modals/hooks";
import { useActiveProject } from "@/features/project/ActiveProject";
import { UpdateProjectStatusModal } from "@/features/project/projectTopSection/stages/UpdateProjectStatusModal";
import { Global, css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { StagesEnum, updateProjectStage } from "@repo/api";
import {
  Button,
  LightMode,
  ListView,
  ListViewItem,
  Variables,
  chevronLeftIcon,
  chevronRightIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  translateStageAction,
  translateStageActionDescription,
  useToast,
} from "@repo/ui";
import { useState } from "react";

import { StageItem } from "./StageItem";
import { handleStageActionClick } from "./stageActionHandlers";
import { useStageNavigationState } from "./useStageNavigationState";

const Stages = styled.ul`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  padding: 6px 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
  scrollbar-width: thin;
  scrollbar-color: ${LightMode.colourNeutral4} transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${LightMode.colourNeutral4};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${LightMode.colourNeutral5};
  }

  @media (max-width: ${Variables.breakpointL}) {
    justify-content: flex-start;
  }
`;

const TopSection = styled.div`
  align-items: center;
  margin-bottom: ${Variables.space100};
  display: flex;
  flex-grow: 1;
  width: 100%;
  gap: ${Variables.space75};

  @media (max-width: ${Variables.breakpointL}) {
    gap: ${Variables.space25};
  }
`;

const StageActions = styled(ListView)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  border: none;
  gap: ${Variables.space100};

  @media (max-width: ${Variables.breakpointL}) {
    grid-template-columns: 1fr;
    gap: ${Variables.space75};
  }
`;

const StageActionItem = styled(ListViewItem)`
  padding: 0 ${Variables.space100};
  border: 1px solid ${LightMode.colourNeutral3};
  border-radius: ${Variables.radiusS};
  transition: all 0.2s ease;

  @media (max-width: ${Variables.breakpointL}) {
    padding: 0 ${Variables.space75};
  }

  &:hover {
    border-color: ${LightMode.colourNeutral5};
  }
`;

const highlightPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(${LightMode.colourInteractiveEmphasisedBorderSelectedRgb}, 0.7);
    background-color: rgba(${LightMode.colourInteractiveEmphasisedBorderSelectedRgb}, 0.1);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(${LightMode.colourInteractiveEmphasisedBorderSelectedRgb}, 0);
    background-color: rgba(${LightMode.colourInteractiveEmphasisedBorderSelectedRgb}, 0.2);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(${LightMode.colourInteractiveEmphasisedBorderSelectedRgb}, 0);
    background-color: transparent;
  }
`;

const globalHighlightStyles = css`
  .highlight-box {
    animation: ${highlightPulse} 2s ease-in-out;
    border-radius: 8px;
  }
`;

export const ProjectStages = () => {
  const { opportunity } = useActiveProject();
  const { updateToast } = useToast();
  const { pushModal } = useModals();

  const [isLoading, setIsLoading] = useState(false);
  const [isClosureModalOpen, setIsClosureModalOpen] = useState(false);

  const currentStage = opportunity?.stageData?.stage ?? StagesEnum.CREATED;

  const {
    stages,
    currentStep,
    salesClosedStep,
    length,
    canGoPrevious,
    canGoNext,
    nextTargetStage,
    shouldOpenClosureModal,
    currentStageActions,
  } = useStageNavigationState({
    currentStage,
    currentStatus: opportunity?.status,
  });

  const handleNextStep = (skipClosureModal = false) => {
    if (shouldOpenClosureModal && !skipClosureModal) {
      setIsClosureModalOpen(true);
      return;
    }

    const targetStage = nextTargetStage;

    if (!targetStage) {
      return;
    }

    if (opportunity?.id) {
      setIsLoading(true);
      updateProjectStage(opportunity.id, targetStage, updateToast).then(() =>
        setIsLoading(false)
      );
    }
  };

  const handlePreviousStep = () => {
    const prevStep = currentStep - 1;
    const prevStepIndex = prevStep - 1;
    const targetStage = stages[prevStepIndex];

    if (!targetStage) {
      return;
    }

    if (opportunity?.id) {
      setIsLoading(true);
      updateProjectStage(opportunity.id, targetStage, updateToast).then(() =>
        setIsLoading(false)
      );
    }
  };

  const handleProjectClosure = () => {
    setIsClosureModalOpen(false);
    handleNextStep(true);
  };

  const handleStageClick = (step: number) => {
    if (isLoading) {
      return undefined;
    }

    if (step === currentStep - 1 && canGoPrevious) {
      return handlePreviousStep;
    }

    if (step === currentStep + 1 && canGoNext) {
      return () => handleNextStep();
    }

    return undefined;
  };

  return (
    <>
      <Global styles={globalHighlightStyles} />
      <HeaderContainer>
        <TopSection>
          <Stages aria-label={t("label.project_stages")}>
            {stages.map((stage, index) => {
              const step = index + 1;

              return (
                <StageItem
                  key={stage}
                  stage={stage}
                  index={index}
                  currentStep={currentStep}
                  salesClosedStep={salesClosedStep}
                  currentStatus={opportunity?.status}
                  isFirst={index === 0}
                  isLast={index === length - 1}
                  onClick={isLoading ? undefined : handleStageClick(step)}
                />
              );
            })}
          </Stages>

          <Button
            ssrIcon={chevronLeftIcon}
            onClick={handlePreviousStep}
            loading={isLoading}
            size="xsmall"
            title={t("label.prev_stage")}
            disabled={!canGoPrevious}
            iconOnly
          />
          <Button
            ssrIcon={chevronRightIcon}
            onClick={() => handleNextStep()}
            loading={isLoading}
            size="xsmall"
            title={t("label.next_stage")}
            disabled={!canGoNext}
            iconOnly
          />
        </TopSection>

        <StageActions
          id="stage-actions-list"
          aria-label={t("label.stage_actions")}
        >
          {currentStageActions.map((action) => (
            <StageActionItem
              control="navigational"
              key={action.action_key}
              id={action.action_key}
              title={translateStageAction(action.action_key)}
              description={
                action.description
                  ? translateStageActionDescription(action.action_key)
                  : undefined
              }
              leadingIcon={action.icon}
              onClick={() =>
                handleStageActionClick({
                  action: action.action,
                  pushModal,
                  opportunity,
                })
              }
            />
          ))}
        </StageActions>
      </HeaderContainer>

      {opportunity && (
        <UpdateProjectStatusModal
          opportunity={opportunity}
          onClose={() => setIsClosureModalOpen(false)}
          visible={isClosureModalOpen}
          onSave={handleProjectClosure}
        />
      )}
    </>
  );
};
